import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    addBlock: string;
    alignmentRotatedTooltip: string;
    alignmentScrollingTooltip: string;
    color: string;
    currentBlockLabel: (blockNumber: number) => string | React.ReactNode;
    customText: string;
    customTextPlaceholder: string;
    delete: string;
    deleteBlock: string;
    display: string;
    errors: {
        /** Error when custom text exceeds character limit */
        customTextTooLong: string;
        /** Error when custom text contains invalid characters */
        invalidCustomText: string;
        /** Error when template name is too long */
        templateNameTooLong: string;
        /** Error when template name already exists */
        templateNameExists: string;
        /** Error when required field is missing */
        required: (field: string) => string | React.ReactNode;
    };
    label: string;
    opacity: string;
    placeholder: string;
    rotation: string;
    selectSize: string;
    sessionBasedWatermarkingLabel: string;
    sessionBasedWatermarkingDescription: string;
    size: string;
    textAlignment: string;
    textScroll: string;
    textShadow: string;
    watermarkDynamicText: (field: string) => string;
    watermarkSize: (size: string) => string;
    ariaLabelAlignCenter: string;
    ariaLabelAlignLeft: string;
    ariaLabelAlignRight: string;
    ariaLabelScrollNone: string;
    ariaLabelScrollRightToLeft: string;
    ariaLabelScrollLeftToRight: string;
    ariaLabelTextShadowOff: string;
    ariaLabelTextShadowOn: string;
};
export type Deps = {
    WATERMARK_STRING_CHAR_LIMIT: any;
};
export default {
    DEFINE(_messages: Text, { WATERMARK_STRING_CHAR_LIMIT }: Deps): void {
        return Object.defineProperties(this, {
            /** Label for adding a block */
            addBlock: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.addBlock?c=2d7943wjhbmwz", defaultMessage: "Add Block" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Tooltip explaining why the alignment control is disabled while a block is rotated */
            alignmentRotatedTooltip: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.alignmentRotatedTooltip?c=106d721ucbjds", defaultMessage: "Rotated text is always center-aligned." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Tooltip explaining why the alignment control is disabled while a block is scrolling */
            alignmentScrollingTooltip: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.alignmentScrollingTooltip?c=2j1z8mp2z6slw", defaultMessage: "Alignment doesn\u2019t apply to scrolling text." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for color selector */
            color: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.color?c=3dcr07ngx58zo", defaultMessage: "Color" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for identifying the current watermark block being edited */
            currentBlockLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (blockNumber: number) => __intl__.intl().formatMessage({ id: "ControlsArea.currentBlockLabel?c=bu50ki4fbks2", defaultMessage: "Block {blockNumber}" }, { blockNumber: blockNumber }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for custom text field */
            customText: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.customText?c=niw51223nicx", defaultMessage: "Custom Text" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Placeholder for custom text input */
            customTextPlaceholder: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.customTextPlaceholder?c=1hvwu5m3l7huf", defaultMessage: "Custom text (optional)" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for delete action */
            delete: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.delete?c=7nzkz7owpk0x", defaultMessage: "Delete" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for deleting a block */
            deleteBlock: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.deleteBlock?c=3ab3x9iwf1sfy", defaultMessage: "Delete block" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for display settings */
            display: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.display?c=1odft4tq6ic35", defaultMessage: "Display" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Error messages for form validation */
            errors: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Error when custom text exceeds character limit */
                        customTextTooLong: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.errors.customTextTooLong?c=8qpjbd1zl53i", defaultMessage: "The name field cannot be more than {WATERMARK_STRING_CHAR_LIMIT} characters" }, { WATERMARK_STRING_CHAR_LIMIT: WATERMARK_STRING_CHAR_LIMIT }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Error when custom text contains invalid characters */
                        invalidCustomText: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.errors.invalidCustomText?c=3jsxsbbqd45e4", defaultMessage: "Only printable unicode characters are allowed." }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Error when template name is too long */
                        templateNameTooLong: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.errors.templateNameTooLong?c=2ocpubow5rbxs", defaultMessage: "Template name is too long" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Error when template name already exists */
                        templateNameExists: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.errors.templateNameExists?c=1fym39gm2vmt6", defaultMessage: "Template name already exists" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Error when required field is missing */
                        required: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (field: string) => __intl__.intl().formatMessage({ id: "ControlsArea.errors.required?c=q1rdf3pqrr6o", defaultMessage: "{field} is required" }, { field: field }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            /** Label for template name field */
            label: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.label?c=1swogad24y3d", defaultMessage: "Template name" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for opacity control */
            opacity: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.opacity?c=1u3hlbq0k6rmq", defaultMessage: "Opacity" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Placeholder for template name input */
            placeholder: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.placeholder?c=1xlj4h5xtnaly", defaultMessage: "Enter template name" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for rotation control */
            rotation: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.rotation?c=1hizmfoubrqf4", defaultMessage: "Rotation" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for size selector */
            selectSize: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.selectSize?c=3385hmfpqlq2k", defaultMessage: "Select size" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for session-based watermarking option */
            sessionBasedWatermarkingLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.sessionBasedWatermarkingLabel?c=2wwngmjs8j0h7", defaultMessage: "Session-based Watermark" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Description of session-based watermarking */
            sessionBasedWatermarkingDescription: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.sessionBasedWatermarkingDescription?c=16g2l4afly3te", defaultMessage: "Embed personal identifying info and custom visuals for enhanced security." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for size control */
            size: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.size?c=38m0cmesgfwnh", defaultMessage: "Size" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for text alignment control */
            textAlignment: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.textAlignment?c=2k9gfxx1tkasb", defaultMessage: "Alignment" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for text scroll control */
            textScroll: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.textScroll?c=35zlyvme0atgg", defaultMessage: "Text Scroll" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for text shadow control */
            textShadow: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.textShadow?c=3vdczjibytgi8", defaultMessage: "Text Shadow" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for dynamic text to add to the watermark */
            watermarkDynamicText: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (field: string): string => __intl__.intl().formatMessage({ id: "ControlsArea.watermarkDynamicText?c=3c9c4r8w79oag", defaultMessage: "{field, select, displayName {Name} displayDateTime {Date & time} displayEmail {Email} displayShareCreator {Share creator''s custom text} displayIP {IP Address} other {Name}}" }, { field: field }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for watermark size */
            watermarkSize: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (size: string): string => __intl__.intl().formatMessage({ id: "ControlsArea.watermarkSize?c=3lj7ifl0d58gs", defaultMessage: "{size, select, SMALL {Small} MEDIUM {Medium} LARGE {Large} HUGE {Huge} other {Medium}}" }, { size: size }));
                },
                enumerable: true,
                configurable: true
            },
            /** Aria label for center text alignment */
            ariaLabelAlignCenter: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.ariaLabelAlignCenter?c=3uii29nb6kq36", defaultMessage: "Align center" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Aria label for left text alignment */
            ariaLabelAlignLeft: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.ariaLabelAlignLeft?c=2tzs0vijxiqe", defaultMessage: "Align left" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Aria label for right text alignment */
            ariaLabelAlignRight: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.ariaLabelAlignRight?c=3434in01vxt6c", defaultMessage: "Align right" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Aria label for no scroll option */
            ariaLabelScrollNone: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.ariaLabelScrollNone?c=2a55dwscpsyga", defaultMessage: "None" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Aria label for right-to-left scroll */
            ariaLabelScrollRightToLeft: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.ariaLabelScrollRightToLeft?c=3iddq4ecarupy", defaultMessage: "Right to Left" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Aria label for left-to-right scroll */
            ariaLabelScrollLeftToRight: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.ariaLabelScrollLeftToRight?c=1w7korvt1d6jo", defaultMessage: "Left to Right" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Aria label for text shadow off */
            ariaLabelTextShadowOff: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.ariaLabelTextShadowOff?c=3qv97vzncag43", defaultMessage: "Disable Text Shadow" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Aria label for text shadow on */
            ariaLabelTextShadowOn: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "ControlsArea.ariaLabelTextShadowOn?c=2ssxnaord3tt2", defaultMessage: "Enable Text Shadow" }));
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps: Deps): void;
};
